import type { CategoryStat } from '@/modules/projects'
import { amountTone, numberToCurrency } from '@/utils'
import { StatTileGrid } from '../../shared'

interface MetadataPanelProps {
  stat: CategoryStat
  outlierCount: number
  outlierAmount: number
}

const TILE_CLASS = 'px-4 py-2.5'
const LABEL_CLASS = 'text-blue-6'
const VALUE_CLASS = 'text-blue-7'

function AbsoluteValueTerm() {
  return (
    <span
      className="cursor-help underline decoration-muted-foreground/60 decoration-dashed underline-offset-2"
      title="The dollar amount with the plus or minus sign removed."
    >
      absolute value
    </span>
  )
}

export const MetadataPanel = ({
  stat,
  outlierCount,
  outlierAmount,
}: MetadataPanelProps) => {
  const outlierRate = stat.count === 0 ? 0 : (outlierCount / stat.count) * 100
  const rateLabel =
    outlierRate === 0
      ? '0%'
      : outlierRate < 0.1
        ? '<0.1%'
        : `${outlierRate.toFixed(1)}%`

  const upperMagnitude = Math.abs(stat.upperBoundOrig)
  const lowerMagnitude = Math.abs(stat.lowerBoundOrig)

  const upperValue = numberToCurrency(upperMagnitude)
  const lowerValue = numberToCurrency(lowerMagnitude)
  const aboveExample = (
    <>
      Flagged when the transaction's <AbsoluteValueTerm /> is larger than{' '}
      {upperValue}.
    </>
  )
  const belowExample = (
    <>
      Flagged when the transaction's <AbsoluteValueTerm /> is smaller than{' '}
      {lowerValue}.
    </>
  )

  const summaryTiles = [
    {
      label: 'Stand out',
      value: outlierCount.toLocaleString(),
      sub: `${rateLabel} of ${stat.count.toLocaleString()} transactions`,
      valueClassName: VALUE_CLASS,
      labelClassName: LABEL_CLASS,
      className: TILE_CLASS,
    },
    {
      label: 'Flagged total',
      value: numberToCurrency(outlierAmount),
      sub: 'Sum of the flagged transactions only',
      valueClassName: amountTone(outlierAmount),
      labelClassName: LABEL_CLASS,
      className: TILE_CLASS,
    },
    {
      label: 'Large limit',
      value: upperValue,
      sub: aboveExample,
      valueClassName: VALUE_CLASS,
      labelClassName: LABEL_CLASS,
      className: TILE_CLASS,
    },
  ]

  const detailTiles = [
    {
      label: 'Category total',
      value: numberToCurrency(stat.totalAmount),
      sub: `Across ${stat.count.toLocaleString()} transactions`,
      valueClassName: amountTone(stat.totalAmount),
      labelClassName: LABEL_CLASS,
      className: TILE_CLASS,
    },
    {
      label: 'Sample size',
      value: stat.count.toLocaleString(),
      sub: 'Transactions analyzed',
      valueClassName: VALUE_CLASS,
      labelClassName: LABEL_CLASS,
      className: TILE_CLASS,
    },
    {
      label: 'Small limit',
      value: lowerValue,
      sub: belowExample,
      valueClassName: VALUE_CLASS,
      labelClassName: LABEL_CLASS,
      className: TILE_CLASS,
    },
  ]

  return (
    <div className="overflow-hidden rounded-lg border">
      <StatTileGrid tiles={summaryTiles} columns={3} />
      <StatTileGrid tiles={detailTiles} columns={3} className="border-t" />
    </div>
  )
}
